import dayjs from 'dayjs'

import type { Patient, UpdatePatientDto } from '@/core/models/patient.model'
import { PatientIdentityStatus } from '@/core/models/patient.model'

import type { ExternalIdentityPatientData } from './external-identity'


export const STRICT_TRAITS = ['birthSurname', 'birthFirstName', 'birthday', 'gender', 'birthPlaceCode'] as const
export type StrictTrait = (typeof STRICT_TRAITS)[number]


export const INS_REQUIRED_USER_INFO_FIELDS = ['birthSurname', 'birthFirstName', 'birthday', 'gender', 'birthPlaceCode'] as const

export type CandidateKind = 'EXACT' | 'PARTIAL'

export type CandidateClassification = {
  kind: CandidateKind
  divergentTraits: StrictTrait[]
}

export type UpdateBlocker = 'DIFFERENT_INS' | 'INS_COLLISION' | 'INS_CALL_BLOCKED' | 'CONNECTOR_UNAVAILABLE'

export function normalizeTrait(value?: string | null): string {
  if (!value) return ''
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toUpperCase()
    .replace(/[-'’\s]+/g, '')
    .trim()
}

function normalizeDate(value?: string | Date | null): string {
  if (!value) return ''
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : ''
}

function getCandidateTrait(candidate: Patient, trait: StrictTrait): string {
  switch (trait) {
    case 'birthSurname':
      return normalizeTrait(candidate.userInfo?.birthSurname || candidate.userInfo?.surname)
    case 'birthFirstName':
      return normalizeTrait(candidate.userInfo?.birthFirstName)
    case 'birthday':
      return normalizeDate(candidate.userInfo?.birthday)
    case 'gender':
      return normalizeTrait(candidate.userInfo?.gender)
    case 'birthPlaceCode':
      return normalizeTrait(candidate.userInfo?.birthPlaceCode)
  }
}

function getReceivedTrait(received: ExternalIdentityPatientData, trait: StrictTrait): string {
  switch (trait) {
    case 'birthSurname':
      return normalizeTrait(received.birthSurname)
    case 'birthFirstName':
      return normalizeTrait(received.birthFirstName)
    case 'birthday':
      return normalizeDate(received.birthday)
    case 'gender':
      return normalizeTrait(received.gender)
    case 'birthPlaceCode':
      return normalizeTrait(received.birthPlaceCode)
  }
}


export function classifyCandidate(received: ExternalIdentityPatientData, candidate: Patient): CandidateClassification {
  const divergentTraits = STRICT_TRAITS.filter((trait) => getCandidateTrait(candidate, trait) !== getReceivedTrait(received, trait))
  return { kind: divergentTraits.length === 0 ? 'EXACT' : 'PARTIAL', divergentTraits }
}

type UpdateBlockerContext = {
  collisionPatient?: Patient | null
  isInsCallBlocked: boolean
  canRunInsVerification: boolean
}

export function getUpdateBlocker(
  received: ExternalIdentityPatientData,
  candidate: Patient,
  { collisionPatient, isInsCallBlocked, canRunInsVerification }: UpdateBlockerContext
): UpdateBlocker | null {
  if (candidate.insNIR && candidate.insNIR !== received.insNIR) return 'DIFFERENT_INS'
  if (collisionPatient && collisionPatient._id !== candidate._id) return 'INS_COLLISION'
  if (isInsCallBlocked) return 'INS_CALL_BLOCKED'
  if (!canRunInsVerification) return 'CONNECTOR_UNAVAILABLE'
  return null
}

export function buildInsOnlyUpdateDto(
  candidate: Patient,
  received: ExternalIdentityPatientData,
  emitter: string
): UpdatePatientDto {
  const missingUserInfo: Record<string, unknown> = {}
  for (const field of INS_REQUIRED_USER_INFO_FIELDS) {
    const current = candidate.userInfo?.[field as keyof typeof candidate.userInfo]
    if (current === undefined || current === null || String(current).trim() === '') {
      missingUserInfo[field] = field === 'birthday' ? received.birthday : received[field as keyof ExternalIdentityPatientData]
    }
  }

  return {
    insNIR: received.insNIR,
    insOID: received.insOID,
    insEmitter: emitter,
    ...(Object.keys(missingUserInfo).length > 0 ? { userInfo: missingUserInfo } : {}),
  } as UpdatePatientDto
}

export function hasVerifiedIns(candidate: Patient): boolean {
  return candidate.identityStatus === PatientIdentityStatus.QUALIFIED || candidate.identityStatus === PatientIdentityStatus.RECOVERED
}

export function isInsAlreadyVerifiedLocally(received: ExternalIdentityPatientData, patient?: Patient | null): boolean {
  if (!patient?.insNIR) return false
  if (patient.insNIR !== received.insNIR) return false
  return hasVerifiedIns(patient)
}
